import { snakeCase } from "lodash";
import { AgGrid } from "@jsq/ds/core";
import { NumberFilterType } from "~/graphql/supergraphEnums";
import {
  type DataVaultEntitiesInput,
  type DataVaultEntitiesQuery,
  type DateFilterType,
  type TextFilterType,
  type ExtensionFieldEnumObjectType,
} from "~/graphql/supergraphTypes";
import { extractDateFromDateTime } from "../../../utils/datetime";
import { transformInvestmentEntitiesToGridRowsWithExtensionFields } from "../../utils/dataTransformers";

/**
 * Factory for converting AG Grid filter/sort models to DataVaultEntities query variables
 * Handles transformation of grid state to GraphQL input types
 */
export class DataVaultEntitiesGQLFactory {
  /**
   * Convert filter type, handling notEqual → notEquals conversion
   * @param filterType - The filter type from AG Grid
   * @returns Converted filter type in snake_case
   */
  private static convertFilterType(filterType: string): string {
    // Convert notEqual to notEquals for non-text filters
    if (filterType === "notEqual") {
      return snakeCase("notEquals");
    }
    return snakeCase(filterType);
  }
  /**
   * Convert AG Grid filter model to DataVaultEntitiesInput
   * @param filterModel - AG Grid filter model
   * @param extensionFields - Extension fields metadata to determine filter types
   * @returns DataVaultEntitiesInput for GraphQL query
   */
  public static toInputFilterVariables(
    filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
    extensionFields?: any[],
  ): DataVaultEntitiesInput {
    const input: DataVaultEntitiesInput = {
      arenaIds: [],
      entityIds: [],
      extensionFieldFilters: undefined,
      includeDeleted: false,
    };

    if (!filterModel || typeof filterModel !== "object") {
      return input;
    }

    // Handle arena filter
    if ("arena" in filterModel && filterModel.arena) {
      input.arenaIds = filterModel.arena;
    }

    // Handle entity name search
    if ("name" in filterModel && filterModel.name) {
      // This is to be removed once common component is updated to use multi-select
      input.entityIds = filterModel.name;
    }

    // Handle extension field filters
    for (const key in filterModel) {
      if (key.startsWith("extensionField_")) {
        const fieldId = key.split("_")[1];
        const filterEntry = (filterModel as any)[key];
        const extensionFieldType = filterEntry?.filterType;
        const filterType = filterEntry?.type;

        switch (extensionFieldType) {
          case "text": {
            input.extensionFieldFilters = {
              ...input.extensionFieldFilters,
              textFilters: [
                ...(input.extensionFieldFilters?.textFilters || []),
                {
                  extensionFieldId: fieldId,
                  text: filterEntry?.filter,
                  textFilterType: snakeCase(filterType) as TextFilterType,
                },
              ],
            };
            break;
          }
          case "date": {
            input.extensionFieldFilters = {
              ...input.extensionFieldFilters,
              dateFilters: [
                ...(input.extensionFieldFilters?.dateFilters || []),
                {
                  extensionFieldId: fieldId,
                  filter: extractDateFromDateTime(filterEntry.dateFrom) ?? "",
                  filterTo: filterEntry.dateTo
                    ? extractDateFromDateTime(filterEntry.dateTo)
                    : null,
                  dateFilterType: this.convertFilterType(filterType) as DateFilterType,
                },
              ],
            };
            break;
          }
          case "number": {
            // Find the extension field to determine if it's integer or float
            const extensionField = extensionFields?.find((f) => f.id === fieldId);
            const fieldTypeId = extensionField?.extensionFieldTypeId;

            // Route to intFilters for INTEGER fields, floatFilters for FLOAT/CURRENCY_USD/PERCENTILE
            const isIntegerField = fieldTypeId === 1; // EXTENSION_FIELD_TYPE_IDS.INTEGER

            if (isIntegerField) {
              input.extensionFieldFilters = {
                ...input.extensionFieldFilters,
                intFilters: [
                  ...(input.extensionFieldFilters?.intFilters || []),
                  {
                    extensionFieldId: fieldId,
                    numberFilterType: this.convertFilterType(filterType) as NumberFilterType,
                    filter: filterEntry.filter ? parseInt(filterEntry.filter, 10) : null,
                    filterTo: filterEntry.filterTo ? parseInt(filterEntry.filterTo, 10) : null,
                  },
                ],
              };
            } else {
              input.extensionFieldFilters = {
                ...input.extensionFieldFilters,
                floatFilters: [
                  ...(input.extensionFieldFilters?.floatFilters || []),
                  {
                    extensionFieldId: fieldId,
                    numberFilterType: this.convertFilterType(filterType) as NumberFilterType,
                    filter: filterEntry.filter ? parseFloat(filterEntry.filter) : null,
                    filterTo: filterEntry.filterTo ? parseFloat(filterEntry.filterTo) : null,
                  },
                ],
              };
            }
            break;
          }
          case "set": {
            // Handle enum filters - AG Grid set filter returns selected labels as array
            const selectedLabels = filterEntry?.values;

            if (selectedLabels && Array.isArray(selectedLabels) && selectedLabels.length > 0) {
              // Find the extension field to map labels back to IDs
              const extensionField = extensionFields?.find((f) => f.id === fieldId);
              const enumValues = extensionField?.enumValues || [];

              // Map selected labels to their corresponding IDs
              const enumValueIds = selectedLabels
                .map((label: string) => {
                  const enumValue = enumValues.find(
                    (ev: ExtensionFieldEnumObjectType) => ev.enumValueLabel === label,
                  );
                  return enumValue?.id;
                })
                .filter((id): id is string => id !== undefined);

              if (enumValueIds.length > 0) {
                input.extensionFieldFilters = {
                  ...input.extensionFieldFilters,
                  enumFilters: [
                    ...(input.extensionFieldFilters?.enumFilters || []),
                    {
                      extensionFieldId: fieldId,
                      enumValueIds: enumValueIds,
                    },
                  ],
                };
              }
            }
            break;
          }
        }
      }
    }

    return input;
  }

  /**
   * Convert offset to cursor (base64 encoded)
   * This matches the GraphQL Relay spec for cursor-based pagination
   * The cursor represents the position AFTER which to start fetching
   * @param offset - The offset (0-based index)
   * @returns Base64 encoded cursor string
   */
  private static offsetToCursor(offset: number): string {
    return btoa(`arrayconnection:${offset}`);
  }

  /**
   * Convert AG Grid pagination parameters to pagination variables
   * Uses offset-based pagination (converted to cursor by backend)
   *
   * Note: The 'after' cursor means "fetch items AFTER this position"
   * So to fetch from offset N, we need to send cursor for position N-1
   *
   * @param offset - Starting row index (0-based)
   * @param pageSize - Number of rows per page (default: 20)
   * @returns Pagination variables { first, after }
   */
  public static toPaginationVariables(
    offset: number = 0,
    pageSize: number = 20,
  ): { first: number; after?: string | null } {
    return {
      first: pageSize,
      after: offset > 0 ? this.offsetToCursor(offset - 1) : null,
    };
  }

  /**
   * Convert DataVaultEntities query response to AG Grid success params
   * @param data - GraphQL query response data
   * @param startRow - The starting row index for this request
   * @returns AG Grid ILoadSuccessParams
   */
  public static toLoadSuccessParams(
    data: DataVaultEntitiesQuery,
    startRow: number = 0,
  ): {
    resp: AgGrid.LoadSuccessParams;
  } {
    const connection = data.dataVaultEntities;

    if (!connection) {
      return {
        resp: {
          rowData: [],
          rowCount: 0,
        },
      };
    }

    const entityNodes = connection.edges.map((edge) => edge.node);
    const rowData = transformInvestmentEntitiesToGridRowsWithExtensionFields(entityNodes);
    const totalCount = connection.count ?? 0;

    // Calculate the actual last row based on what we received
    // If hasNextPage is false, the last row is startRow + rowData.length
    const lastRow =
      connection.pageInfo?.hasNextPage === false ? startRow + rowData.length : undefined;

    return {
      resp: {
        rowData,
        rowCount: lastRow ?? totalCount,
      },
    };
  }

  /**
   * Convert AG Grid sort model to GraphQL sort variables
   * Maps AG Grid column IDs to backend field names
   */
  public static toSortVariables(sortModel: AgGrid.SortModelItem[] | null | undefined): {
    sortBy?: string;
    sortDirection?: string;
  } {
    if (!sortModel || sortModel.length === 0) {
      return {};
    }

    const firstSort = sortModel[0];
    let sortBy: string | undefined;

    if (firstSort.colId === "arena") {
      sortBy = "arena_name";
    } else if (firstSort.colId === "name") {
      sortBy = "name";
    }

    if (!sortBy) {
      return {};
    }

    return {
      sortBy,
      sortDirection: firstSort.sort === "asc" ? "ASC" : "DESC",
    };
  }
}
